import Label from 'LuxKit/Label'
import PromotionLabel from 'LuxKit/Label/PromotionLabel'
import Caption from 'LuxKit/Typography/Caption'
import { useOfferBestPrice } from 'hooks/Offers/useOfferBestPrice'
import { memo, useMemo } from 'react'
import { FormattedMessage } from 'react-intl'
import useOfferMetaData from 'hooks/Offers/useOfferMetaData'
import { OFFER_BOOKINGS_THRESHOLD } from 'constants/offer'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import { getShouldUseSearchPrices } from 'lib/search/searchUtils'
import OfferUrgencyCountdownLabel from 'components/Common/Labels/OfferUrgencyCountdownLabel'
import {
  PRIOR_TO_CHECK_IN_CANCELLATION_POLICIES,
} from 'constants/cancellationPolicies'
import { getRefundableUntilAndIsRefundableFromCancellationPolicy } from 'components/SearchV2/SearchTileCancellationPolicy/TileCancellationPolicy'

const URGENCY_TAG_THRESHOLD = 5
const COMPACT_URGENCY_TAG_LIMIT = 2

type CompactUrgencyTagType = 'countdown' | 'flexibleCancellation' | 'lowRooms' | 'bookings'

const COMPACT_URGENCY_TAG_PRIORITY: Array<CompactUrgencyTagType> = [
  'countdown',
  'flexibleCancellation',
  'lowRooms',
  'bookings',
]

interface Props {
  offer: App.Offer | App.OfferSummary
  filters?: App.OfferListFilters
  cancellationPolicyType?: App.CancellationPolicyType
  timezoneOffset?: number
  variant?: 'default' | 'compact'
}

interface CompactUrgencyTagOptions {
  hasCountdown: boolean
  hasFlexibleCancellation: boolean
  hasLowRooms: boolean
  hasBookings: boolean
}

export function getCompactUrgencyTagTypes(options: CompactUrgencyTagOptions): Array<CompactUrgencyTagType> {
  const visibilityByType: Record<CompactUrgencyTagType, boolean> = {
    countdown: options.hasCountdown,
    flexibleCancellation: options.hasFlexibleCancellation,
    lowRooms: options.hasLowRooms,
    bookings: options.hasBookings,
  }

  return COMPACT_URGENCY_TAG_PRIORITY
    .filter(type => visibilityByType[type])
    .slice(0, COMPACT_URGENCY_TAG_LIMIT)
}

export function getShouldShowFlexibleCancellationTag({
  cancellationPolicyType,
  checkInDate,
  timezoneOffset,
}: {
  cancellationPolicyType?: App.CancellationPolicyType
  checkInDate?: string
  timezoneOffset?: number
}) {
  if (!cancellationPolicyType || !PRIOR_TO_CHECK_IN_CANCELLATION_POLICIES.includes(cancellationPolicyType)) {
    return false
  }

  const { isRefundable } = getRefundableUntilAndIsRefundableFromCancellationPolicy(
    cancellationPolicyType,
    checkInDate || '',
    timezoneOffset || 0,
  )

  return isRefundable
}

function OfferTilePricingUrgencyTags(props: Props) {
  const {
    cancellationPolicyType,
    offer,
    filters,
    timezoneOffset,
    variant = 'default',
  } = props

  const numRoomsPackage = useOfferBestPrice(offer, filters, { disabled: getShouldUseSearchPrices(filters) })
  const offerMetaData = useOfferMetaData(offer.id, filters)

  const showBookedTodayUrgencyTag = offer.stats.booked >= OFFER_BOOKINGS_THRESHOLD

  const availableRooms = numRoomsPackage?.availableRooms ?? offerMetaData?.availableRooms
  const hasLowRoomsUrgencyTag =
    !!availableRooms &&
    availableRooms !== 0 &&
    availableRooms <= URGENCY_TAG_THRESHOLD &&
    !!filters?.checkIn
  const showLowRoomsUrgencyTag = !showBookedTodayUrgencyTag && hasLowRoomsUrgencyTag

  const countdownUrgencyLabel = useMemo(() => {
    return offer.urgencyTags?.find(label => label.type === 'left' && label.end)
  }, [offer.urgencyTags])
  const showFlexibleCancellationTag = getShouldShowFlexibleCancellationTag({
    cancellationPolicyType,
    checkInDate: filters?.checkIn,
    timezoneOffset,
  })
  const compactTagTypes = useMemo(() => getCompactUrgencyTagTypes({
    hasBookings: showBookedTodayUrgencyTag,
    hasCountdown: !!countdownUrgencyLabel,
    hasFlexibleCancellation: showFlexibleCancellationTag,
    hasLowRooms: showLowRoomsUrgencyTag,
  }), [countdownUrgencyLabel, showLowRoomsUrgencyTag, showBookedTodayUrgencyTag, showFlexibleCancellationTag])

  if (variant === 'compact') {
    return <>
      {compactTagTypes.map((type) => {
        if (type === 'countdown' && countdownUrgencyLabel?.end) {
          return <OfferUrgencyCountdownLabel
            key={type}
            endDate={countdownUrgencyLabel.end}
            tagPlace="offer-labels"
          />
        }
        if (type === 'flexibleCancellation') {
          return <PromotionLabel key={type} variant="default">
            <Caption variant="medium" weight="bold">
              <FormattedMessage
                id="hCwWYZZ-Qge1iETJVsZy"
                defaultMessage="Flexible cancellation"
                description="Highlight text for flexible cancellation policy on search tiles"
              />
            </Caption>
          </PromotionLabel>
        }
        if (type === 'lowRooms' && availableRooms) {
          return <Label key={type} variant="urgency" kind="plain">
            <FormattedMessage
              id="YWK-9mp9BmGtOL4GLm3i"
              defaultMessage="Only {availableRooms, plural, one {# room} other {# rooms}} left at this price!"
              description="Urgency tag showing limited rooms remaining at the current price"
              values={{ availableRooms }}
            />
          </Label>
        }
        if (type === 'bookings') {
          return <BookingsCountLabel key={type} bookings={offer.stats.booked} threshold="offer" />
        }
        return null
      })}
    </>
  }

  return <>
    <BookingsCountLabel bookings={offer.stats.booked} threshold="offer" />
    {showLowRoomsUrgencyTag && <Label variant="urgency" kind="plain">
      <FormattedMessage
        id="YWK-9mp9BmGtOL4GLm3i"
        defaultMessage="Only {availableRooms, plural, one {# room} other {# rooms}} left at this price!"
        description="Urgency tag showing limited rooms remaining at the current price"
        values={{ availableRooms }}
      />
    </Label>}
  </>
}

export default memo(OfferTilePricingUrgencyTags)
